/* eslint-disable react-refresh/only-export-components -- AuthProvider + useAuth hook live together by convention */
import React from 'react';
import type { Session, User as SupabaseAuthUser } from '@supabase/supabase-js';
import { selfServiceSupabase } from '../../../lib/selfServiceSupabase';
import { apiClient, ApiError } from '../../../lib/apiClient';
import { isSelfServiceDevelopment } from '../../../lib/env';
import * as analytics from '../../../lib/analytics';
import { stationTeamService } from '../../../services/stationTeamService';
import { EVENTS } from '../../../lib/analytics';
import BusinessDecisionModal from './BusinessDecisionModal';
import { clearAdminScopeMemory } from '../admin/lastAdminStation';
import {
  normalizeBusinessName,
  saveWizardContact,
  type EnsureSeatOutcome,
  type PendingBusinessDecision,
  type WizardBusinessIntent,
} from './businessDecision';

// Real Supabase Auth replaces the previous localStorage-based mock.
//
//   - requestOtp → POST /auth/sign-in/request (selfservice-api mints the
//                  Supabase token via admin.generateLink + dispatches our
//                  SendGrid `auth.signin` template — see ADR 0001)
//   - verifyOtp  → supabase.auth.verifyOtp({ email, token, type: 'email' })
//   - identity   → supabase.auth.getSession() / onAuthStateChange (real JWT)
//
// The session JWT is consumed by the selfservice-api NestJS service via the
// centralized apiClient. Profile-side fields (business_name, contact_name…)
// are still kept in a local React state slot until the corresponding
// /profiles API endpoints are wired up in Phase 4 of the migration.

export type SelfServiceRole = 'advertiser' | 'publisher' | 'admin';

// The raw `app_metadata.role` from the Supabase JWT. Kept alongside the
// coarse `role` because the UI sometimes needs to distinguish org_admin from
// manager/sales_rep (both surface as 'publisher' in the simplified role).
export type SelfServiceRawRole =
  | 'super_admin'
  | 'organization_admin'
  | 'manager'
  | 'sales_rep'
  | 'advertiser';

// Per-Supabase-user identity exposed to the rest of the app. Business identity
// (business name + website) is no longer a user field — it lives on the
// advertiser seat (see AdvertiserMembership.advertiserName / businessWebsite).
// Personal contact info comes from /profiles/me.
export interface SelfServiceUser {
  id: string;
  email: string;
  contactName?: string;
  contactPhone?: string;
  role?: SelfServiceRole;
  rawRole?: SelfServiceRawRole;
  organizationId?: string;
  publisherSlug?: string; // populated when the JWT scopes the user to a station
  /**
   * Whether the caller's `auth.users` row has an `encrypted_password`. Hydrated
   * from `GET /auth/me/has-password` on session change. `null` while the
   * initial fetch is in flight — components deciding whether to nudge the
   * user to set a password should treat null as "don't show it yet."
   */
  hasPassword: boolean | null;
}

// CTV-415 seat redesign: the advertiser is a shared seat. Multi-membership is
// allowed (any user can be on any number of advertiser seats), so the wizard
// has to track which seat is currently selected. Selection persists in
// localStorage (`SELECTED_ADVERTISER_KEY`) so a returning user lands on the
// same seat they last used.
export interface AdvertiserMembership {
  membershipId: string;
  advertiserId: string;
  advertiserName: string;
  businessWebsite: string | null;
  role: string;
  acceptedAt: string | null;
}

// CTV-415 — stations the signed-in staff user can administer (driven by
// JWT scoping + the publisher_admin_grants check). Fetched once on session
// change; powers the /admin picker and the sidebar station switcher.
export interface AdminStation {
  slug: string | null;
  name: string;
  displayName: string | null;
  organizationId: string;
}

interface AuthContextValue {
  user: SelfServiceUser | null;
  isAuthenticated: boolean;
  /**
   * CTV-415 Phase 2. Accepted advertiser memberships for the signed-in user.
   * `null` while the initial /memberships/mine fetch is in flight; `[]` when
   * the user has no memberships (e.g. publisher staff, super_admin, or a
   * fresh advertiser who hasn't claimed their first submission yet).
   */
  advertiserMemberships: AdvertiserMembership[] | null;
  /** Force a refetch of /memberships/mine, e.g. right after accepting an invite. */
  refreshMemberships: () => Promise<void>;
  /**
   * CTV-582 "eager membership seed": inject a membership directly into the
   * `advertiserMemberships` list (upsert by advertiserId) without waiting for
   * a follow-up /memberships/mine round-trip. Used by callers of
   * POST /memberships/ensure-self so the wizard body can rebuild
   * `selectedAdvertiser` in the same tick the seat is created, closing the
   * post-verify hydration handoff race described in
   * `CONTEXT.md → Post-verify hydration (CTV-582)`.
   */
  seedMembership: (m: AdvertiserMembership) => void;
  /**
   * The advertiser the user is currently "acting as." Derived from
   * advertiserMemberships + selectedAdvertiserId. `null` when the user has no
   * memberships (anon, or first-time buyer who hasn't completed step 4 yet).
   */
  selectedAdvertiser: AdvertiserMembership | null;
  /** Switch the active seat. Persists to localStorage. */
  setSelectedAdvertiserId: (advertiserId: string) => void;
  /**
   * CTV-638. The wizard body publishes what the buyer has typed into step 1
   * (live RHF values) so the provider can act on it after a sign-in destroys
   * and rebuilds that body. Call with `null` on unmount. Held in a ref, so
   * this does NOT re-render the tree on every keystroke.
   */
  registerWizardIntent: (intent: WizardBusinessIntent | null) => void;
  /**
   * CTV-638. The single entry point for "the wizard needs an advertiser seat."
   * Resolves to the seat to attach the campaign to, asking the buyer first
   * (BusinessDecisionModal) when the business they typed isn't one they
   * already have. `{ status: 'deferred' }` means they chose to go back and fix
   * the name — the wizard returns them to step 1 and re-calls this on the next
   * Continue.
   */
  ensureSeatForWizard: (intent: WizardBusinessIntent) => Promise<EnsureSeatOutcome>;
  /**
   * CTV-638. Non-null while the business decision is open. Drives the modal,
   * which AuthProvider renders itself (the wizard can't own it — the sign-in
   * that raises the question remounts the wizard body).
   */
  pendingBusinessDecision: PendingBusinessDecision | null;
  /**
   * CTV-415 Phase 2b. Whether the signed-in user has an active publisher-admin
   * grant. `null` while the RPC is in flight; `true` / `false` thereafter.
   * Errors deny by default (i.e. surface as `false`) so a transient supabase
   * outage can't accidentally hand out wizard admin access.
   */
  hasPublisherAdminGrant: boolean | null;
  /** Refetch /profiles/me, e.g. after BusinessInfoStep commits new fields. */
  refreshProfile: () => Promise<void>;
  /**
   * CTV-415 — stations the signed-in staff user can administer. `null` while
   * the initial fetch is in flight; `[]` when the user has no admin access
   * (advertisers, ungranted staff, signed-out users).
   */
  myAdminStations: AdminStation[] | null;
  refreshMyAdminStations: () => Promise<void>;
  /**
   * Force a refetch of GET /auth/me/has-password. Called by SignInModal
   * immediately after a successful OTP verify (to decide whether to show the
   * "Set a password" nudge) and after the user finishes setting one, so the
   * whole app sees the fresh value without waiting for the background effect.
   * Returns the fresh value (or `null` on error / signed-out) so callers can
   * branch on it without waiting for a re-render.
   */
  refreshHasPassword: () => Promise<boolean | null>;
  signOut: () => Promise<void>;
  /** Persist a profile patch to selfservice_advertiser_profiles. Requires sign-in. */
  updateProfile: (patch: Partial<SelfServiceUser>) => Promise<void>;
  /** Sends the OTP via Supabase Auth. devCode is no longer available — kept for shape compatibility. */
  requestOtp: (email: string, stationSlug?: string) => Promise<{ devCode: string }>;
  verifyOtp: (email: string, code: string) => Promise<SelfServiceUser>;
  /**
   * True when the signed-in user is allowed to administer the given station.
   * - super_admin: any station
   * - organization_admin: stations whose organizationId matches user.organizationId
   * - manager / sales_rep: stations whose `name` is in app_metadata.stations
   * - everyone else: false
   */
  canAdminStation: (config: {
    name: string;
    organizationId: string | null;
  }) => boolean;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

// Keys we wipe on sign-out — drafts and code-attribution localStorage entries
// belong to the previous tab/user and should not bleed into the next session.
const DRAFT_PREFIX = 'ctvbuyer.selfservice.draft.';
const CODE_PREFIX_REF = 'selfservice:ref-code:';
const CODE_PREFIX_PROMO = 'selfservice:promo-code:';
// Currently-selected advertiser seat (per-browser). Persists across reloads
// so users land on the same seat they last used. Cleared on sign-out.
const SELECTED_ADVERTISER_KEY = 'ctvbuyer.selfservice.selectedAdvertiserId';

function clearWizardCaches() {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(DRAFT_PREFIX) ||
         k.startsWith(CODE_PREFIX_REF) ||
         k.startsWith(CODE_PREFIX_PROMO))
      ) {
        toRemove.push(k);
      }
    }
    for (const k of toRemove) localStorage.removeItem(k);
    localStorage.removeItem(SELECTED_ADVERTISER_KEY);
  } catch { /* private mode — non-fatal */ }
}

function readSelectedAdvertiserId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ADVERTISER_KEY);
  } catch {
    return null;
  }
}

function writeSelectedAdvertiserId(id: string | null) {
  try {
    if (id) localStorage.setItem(SELECTED_ADVERTISER_KEY, id);
    else localStorage.removeItem(SELECTED_ADVERTISER_KEY);
  } catch { /* private mode — non-fatal */ }
}

// Map Supabase JWT app_metadata.role → the simpler SelfServiceRole shown
// to UI components. Manager / sales_rep / organization_admin all surface as
// 'publisher' (they administer one or more stations); super_admin is 'admin';
// 'advertiser' is the default for the consumer portal.
function mapRole(authUser: SupabaseAuthUser | null | undefined): SelfServiceRole | undefined {
  const raw = authUser?.app_metadata?.role;
  if (raw === 'super_admin') return 'admin';
  if (raw === 'organization_admin' || raw === 'manager' || raw === 'sales_rep') return 'publisher';
  if (raw === 'advertiser') return 'advertiser';
  return undefined;
}

const RAW_ROLES: readonly SelfServiceRawRole[] = [
  'super_admin',
  'organization_admin',
  'manager',
  'sales_rep',
  'advertiser',
];

// The subset of raw roles that map to CTV Buyer "staff" — used by gates on
// /onboard, /settings/security, and the staff sign-in flow. Exported so the
// three consumer sites don't each maintain their own Set literal.
export const STAFF_RAW_ROLES: ReadonlySet<SelfServiceRawRole> = new Set([
  'super_admin',
  'organization_admin',
  'manager',
  'sales_rep',
]);

function extractRawRole(authUser: SupabaseAuthUser | null | undefined): SelfServiceRawRole | undefined {
  const raw = authUser?.app_metadata?.role;
  return typeof raw === 'string' && (RAW_ROLES as readonly string[]).includes(raw)
    ? (raw as SelfServiceRawRole)
    : undefined;
}

// CTV-615: organization_id is an authorization input and lives in app_metadata
// (service-role writable only). user_metadata is self-writable, so reading org
// from it would let a user display, and act against, a forged organization.
// This is display-only, but it must not show a forged org. See ADR-0024.
function extractOrganizationId(authUser: SupabaseAuthUser | null | undefined): string | undefined {
  const orgId = authUser?.app_metadata?.organization_id;
  return typeof orgId === 'string' && orgId.length > 0 ? orgId : undefined;
}

// JWT carries station NAMES in app_metadata. We surface the first one as a
// "current" publisherSlug for the UI to match against the URL slug. The legacy
// singular `station` is gone: the CTV-615 backfill promoted it to a one-element
// `stations` array.
function extractStations(authUser: SupabaseAuthUser | null | undefined): string[] {
  const meta = (authUser?.app_metadata ?? {}) as Record<string, unknown>;
  if (Array.isArray(meta.stations)) {
    return (meta.stations as unknown[]).filter((x): x is string => typeof x === 'string' && x.length > 0);
  }
  return [];
}

// DB-backed shape returned from GET /profiles/me. Mirrors the
// selfservice_advertiser_profiles row in lowercase camelCase. Null when
// the user has not yet saved a profile. Seat redesign: businessName +
// businessWebsite moved to the advertiser; profile is just personal contact
// info now.
interface AdvertiserProfile {
  userId: string;
  contactEmail: string;
  contactName: string | null;
  contactPhone: string | null;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = React.useState<Session | null>(null);
  // Persisted profile for the signed-in user (DB-backed via /profiles/me).
  // Replaced the previous in-memory `profilesByEmail` map so business info
  // survives sign-out / new-browser / refresh. Null while loading or when
  // the user has not yet completed BusinessInfoStep.
  const [profile, setProfile] = React.useState<AdvertiserProfile | null>(null);
  const [bootstrapped, setBootstrapped] = React.useState(false);
  const [advertiserMemberships, setAdvertiserMemberships] = React.useState<
    AdvertiserMembership[] | null
  >(null);
  // CTV-415 seat redesign: the currently-active advertiser seat. Hydrated
  // from localStorage on first mount; validated against the memberships list
  // (an obsolete id gets cleared and replaced with the first available one).
  const [selectedAdvertiserId, setSelectedAdvertiserIdState] = React.useState<string | null>(
    () => readSelectedAdvertiserId(),
  );
  // Phase 2b RPC: gates publisher-staff access to wizard admin views. `null`
  // while the RPC is in flight, then true/false. Errors deny by default — see
  // the effect below.
  const [hasPublisherAdminGrant, setHasPublisherAdminGrant] = React.useState<boolean | null>(
    null,
  );
  // Stations the user can administer (intersection of JWT scope + grant).
  // `null` while loading; `[]` when no access. Drives /admin picker UX.
  const [myAdminStations, setMyAdminStations] = React.useState<AdminStation[] | null>(null);
  // Whether the signed-in user has an `encrypted_password` on their auth row.
  // `null` while loading or signed-out; true/false once /auth/me/has-password
  // resolves. Drives the post-OTP "Set a password" nudge in SignInModal.
  const [hasPassword, setHasPassword] = React.useState<boolean | null>(null);

  // Bootstrap: read the existing session, then subscribe to changes.
  React.useEffect(() => {
    let cancelled = false;
    if (!selfServiceSupabase) {
      setBootstrapped(true);
      return;
    }
    selfServiceSupabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setBootstrapped(true);
    }).catch(() => {
      if (cancelled) return;
      setBootstrapped(true);
    });
    const { data: sub } = selfServiceSupabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // CTV-468 analytics: identify by Supabase user id (no PII) once a session
  // exists; reset on sign-out. auth_state rides on every event as context.
  // analytics.identify is internally idempotent, so the per-route AuthProvider
  // re-mount doesn't re-fire it on each navigation.
  React.useEffect(() => {
    if (!bootstrapped) return;
    const userId = session?.user?.id;
    if (userId) {
      analytics.identify(userId);
      // role + publisher_slug ride on every event so advertiser / publisher /
      // admin audiences are separable in PostHog (and the advertiser funnels
      // can be filtered to role=advertiser to drop publisher/staff noise).
      analytics.registerContext({
        auth_state: 'authenticated',
        // Any authenticated non-staff user is an advertiser on this portal;
        // mapRole only returns publisher/admin/explicit-advertiser, so default
        // the rest to 'advertiser' to keep the super-property always set.
        role: mapRole(session?.user) ?? 'advertiser',
        publisher_slug: extractStations(session?.user)[0] ?? null,
      });
    } else {
      analytics.reset();
      analytics.registerContext({ auth_state: 'anonymous' });
    }
  }, [session?.user, bootstrapped]);

  const buildUser = React.useCallback(
    (authUser: SupabaseAuthUser | null | undefined): SelfServiceUser | null => {
      if (!authUser?.email) return null;
      const stations = extractStations(authUser);
      // Profile fields come from the DB-backed `profile` state. They're empty
      // strings / undefined until /profiles/me resolves; BusinessInfoStep
      // refills them once it does.
      const profileForUser = profile?.userId === authUser.id ? profile : null;
      return {
        id: authUser.id,
        email: authUser.email,
        contactName: profileForUser?.contactName ?? undefined,
        contactPhone: profileForUser?.contactPhone ?? undefined,
        role: mapRole(authUser),
        rawRole: extractRawRole(authUser),
        organizationId: extractOrganizationId(authUser),
        publisherSlug: stations[0],
        hasPassword,
      };
    },
    [profile, hasPassword],
  );

  const user = React.useMemo(() => buildUser(session?.user), [session, buildUser]);

  // Fetch /memberships/mine whenever the authenticated user changes. The
  // result drives advertiser-side routing: a user with at least one accepted
  // membership lands on /my-campaigns; without one, they only see the wizard
  // submission flow (publisher staff also use this — they'll typically have
  // zero memberships but still pass through publisher admin paths).
  //
  // CTV-582: reads the current user id from the live Supabase session at
  // call time instead of the React `user` snapshot. The wizard's `onVerified`
  // handler captures this callback on the pre-verify render, when the React
  // `user` is still null — a captured-`user?.id` gate would trip and clobber
  // `advertiserMemberships` back to null right after the seat was created.
  // The live session is populated the moment `verifyOtp` returns, so the
  // getSession() read here always sees the freshly-signed-in user id.
  // Deps are `[]` so the reference is stable — no captured state to go stale.
  const refreshMemberships = React.useCallback(async () => {
    if (!selfServiceSupabase) {
      setAdvertiserMemberships(null);
      return;
    }
    const { data } = await selfServiceSupabase.auth.getSession();
    const currentUserId = data.session?.user?.id;
    if (!currentUserId) {
      setAdvertiserMemberships(null);
      return;
    }
    try {
      const resp = await apiClient.get<{ memberships: AdvertiserMembership[] }>(
        '/memberships/mine',
      );
      setAdvertiserMemberships(resp.memberships ?? []);
    } catch (e) {
      // Don't blow up the UI for a memberships fetch failure — leaves
      // advertiserMemberships at its previous value. Components that hard-
      // depend on memberships should render a loading / retry state when
      // it's null and the user is authenticated.
      console.warn('[useAuth] memberships fetch failed:', e);
    }
  }, []);

  // CTV-582 "eager membership seed": upsert a membership into the list by
  // advertiserId. Used right after POST /memberships/ensure-self returns so
  // the wizard body sees `selectedAdvertiser` populated in the same commit
  // that flips `advertiserId` non-null — no /memberships/mine round-trip,
  // no race with a stale-closure refresh.
  const seedMembership = React.useCallback((m: AdvertiserMembership) => {
    setAdvertiserMemberships((prev) => {
      const list = prev ? [...prev] : [];
      const i = list.findIndex((x) => x.advertiserId === m.advertiserId);
      if (i >= 0) list[i] = m;
      else list.push(m);
      return list;
    });
  }, []);

  React.useEffect(() => {
    if (!user?.id) {
      setAdvertiserMemberships(null);
      return;
    }
    void refreshMemberships();
  }, [user?.id, refreshMemberships]);

  const setSelectedAdvertiserId = React.useCallback((advertiserId: string) => {
    setSelectedAdvertiserIdState(advertiserId);
    writeSelectedAdvertiserId(advertiserId);
  }, []);

  const selectedAdvertiser = React.useMemo<AdvertiserMembership | null>(() => {
    if (!advertiserMemberships || !selectedAdvertiserId) return null;
    return advertiserMemberships.find((m) => m.advertiserId === selectedAdvertiserId) ?? null;
  }, [advertiserMemberships, selectedAdvertiserId]);

  // Fetch the DB-backed profile every time the authenticated user changes.
  // Replaces the previous in-memory `profilesByEmail` shim so business info
  // persists across sign-outs and devices.
  const refreshProfile = React.useCallback(async () => {
    if (!session?.user?.id) {
      setProfile(null);
      return;
    }
    try {
      const data = await apiClient.get<AdvertiserProfile | null>('/profiles/me');
      setProfile(data ?? null);
    } catch (e) {
      // 404 is the not-yet-saved case — service returns null instead, but
      // surface other errors so we know if the API is down.
      console.warn('[useAuth] profile fetch failed:', e);
    }
  }, [session?.user?.id]);

  React.useEffect(() => {
    if (!session?.user?.id) {
      setProfile(null);
      return;
    }
    void refreshProfile();
  }, [session?.user?.id, refreshProfile]);

  // ===========================================================================
  // Station team claims — pre-authorized access applied at first sign-in.
  //
  // When a publisher lists someone on their onboarding form, finalize writes a
  // station_team_claims row keyed on that email. Applying it mutates
  // app_metadata (role, organization_id, stations[]) SERVER-side — but the JWT
  // in hand was minted at sign-in, before any of that existed. rawRole is read
  // off that token, so without a refresh the user is granted access they can't
  // see and /admin/:slug turns them away.
  //
  // So: apply, and only if something was actually granted, mint a fresh token.
  // ===========================================================================
  const claimsCheckedForUser = React.useRef<string | null>(null);
  React.useEffect(() => {
    const userId = session?.user?.id;
    if (!userId) {
      claimsCheckedForUser.current = null;
      return;
    }
    // Once per user. refreshSession() below re-fires this effect, and without
    // the guard a granted claim would loop.
    if (claimsCheckedForUser.current === userId) return;
    claimsCheckedForUser.current = userId;

    void (async () => {
      try {
        const result = await stationTeamService.applyClaims();

        // Refresh when the server's view of our access is ahead of the token,
        // not merely when this call changed something. If an earlier call
        // applied a claim but its response was lost, `applied` comes back 0
        // while we still hold a role-less token — without this comparison the
        // user stays locked out of their own station until it expires.
        const meta = (session?.user?.app_metadata ?? {}) as Record<string, unknown>;
        const tokenStations = Array.isArray(meta.stations) ? (meta.stations as string[]) : [];
        const tokenRole = typeof meta.role === 'string' ? meta.role : null;
        const serverStations = result.access?.stations ?? [];
        const stale =
          result.applied > 0 ||
          (result.access != null &&
            (result.access.role !== tokenRole ||
              serverStations.some((s) => !tokenStations.includes(s))));

        if (stale) {
          // Picks up the role + stations[] the claim wrote.
          await selfServiceSupabase?.auth.refreshSession();
        }
      } catch (e) {
        // Never block sign-in on this: the claim stays pending and applies on
        // the next boot via GET /profiles/me.
        console.warn('[useAuth] claim application failed:', e);
      }
    })();
    // app_metadata is a dep because the staleness comparison reads it. It gets
    // a new identity on every refresh, so this effect re-fires — the per-user
    // ref guard above is what keeps a granted claim from looping.
  }, [session?.user?.id, session?.user?.app_metadata]);

  // ===========================================================================
  // CTV-638 — the mid-wizard business decision.
  //
  // A buyer types a business at step 1, then signs into an account that
  // already has advertiser seats. We must not silently adopt one of those
  // seats (that shipped an "Apple Garden" campaign as an "RI Breakers" one).
  //
  // This lives in the provider, not the wizard, for two reasons: the wizard
  // body is keyed by `${advertiserId}:${userId}` and is therefore destroyed by
  // the very sign-in that raises the question, and the header's SignInModal is
  // a sibling ABOVE the wizard. The provider outlives both and is in scope for
  // both of SignInModal's success paths (password + OTP) — hooking `verifyOtp`
  // alone would miss the password path.
  // ===========================================================================

  // What the buyer has typed into step 1, live. A ref, not state: the wizard
  // re-registers on every keystroke and this must not re-render the tree.
  const wizardIntentRef = React.useRef<WizardBusinessIntent | null>(null);
  // Mirror of advertiserMemberships for the callbacks below, which are captured
  // by the wizard on its pre-sign-in render (the same stale-closure trap
  // refreshMemberships documents above).
  const membershipsRef = React.useRef<AdvertiserMembership[] | null>(advertiserMemberships);
  React.useEffect(() => {
    membershipsRef.current = advertiserMemberships;
  }, [advertiserMemberships]);

  const [pendingBusinessDecision, setPendingBusinessDecision] =
    React.useState<PendingBusinessDecision | null>(null);
  // Held open while the modal is up; settled by the buyer's answer.
  const decisionResolverRef = React.useRef<((o: EnsureSeatOutcome) => void) | null>(null);
  // Both triggers (the wizard's step-4 verify handler AND the sign-in effect
  // below) fire for the same OTP verify. The second caller gets the first's
  // promise instead of racing a second ensure-self POST.
  const ensureSeatInFlightRef = React.useRef<Promise<EnsureSeatOutcome> | null>(null);

  // "A business decision is unresolved" — true while the modal is up AND while
  // it's deferred (the buyer went back to fix a typo). It suppresses the
  // auto-select effect further down: leaving `selectedAdvertiserId` null is
  // what keeps `selectedAdvertiser` null, which is what stops the wizard's seed
  // from overwriting the business the buyer typed with a seat they didn't pick.
  //
  // State AND ref. The auto-select effect runs in the SAME commit as the
  // trigger effect that opens the decision, so a state read there would still
  // see `false`; the ref is written synchronously, before any await.
  const [seatDecisionOpen, setSeatDecisionOpen] = React.useState(false);
  const seatDecisionOpenRef = React.useRef(false);
  const openSeatDecision = React.useCallback(() => {
    seatDecisionOpenRef.current = true;
    setSeatDecisionOpen(true);
  }, []);
  const closeSeatDecision = React.useCallback(() => {
    seatDecisionOpenRef.current = false;
    setSeatDecisionOpen(false);
  }, []);

  // Registered by the wizard's BODY on every keystroke, but cleared only by the
  // wizard's WRAPPER, on real wizard exit. That split is load-bearing: signing
  // in flips `userId`, which is half the body's identity key, so the body
  // unmounts and stays unmounted across an async draft reload. If the body's
  // own cleanup cleared the intent, it would be gone by the time
  // /memberships/mine resolves — and the decision effect below would find
  // nothing to decide about and let the auto-select adopt a seat. That is
  // precisely the bug, just moved.
  const registerWizardIntent = React.useCallback((intent: WizardBusinessIntent | null) => {
    if (intent) {
      // Ignore a degraded re-registration. The body remounts mid-sign-in and its
      // first commit under the new identity runs against an EMPTY form — the
      // draft for the new scope hasn't loaded yet — so it re-publishes a blank
      // business over the one the buyer typed. Observed in dev on the header
      // sign-in path. Keep the last real value; the body re-publishes the truth
      // a beat later, once its draft lands.
      const held = wizardIntentRef.current;
      if (
        !intent.businessName.trim() &&
        held?.slug === intent.slug &&
        held.businessName.trim()
      ) {
        return;
      }
      wizardIntentRef.current = intent;
      return;
    }
    wizardIntentRef.current = null;
    // The buyer left the wizard. Settle any decision they walked away from.
    setPendingBusinessDecision(null);
    closeSeatDecision();
    const resolve = decisionResolverRef.current;
    decisionResolverRef.current = null;
    resolve?.({ status: 'deferred' });
  }, [closeSeatDecision]);

  // The memberships list may not have landed yet when the wizard's step-4
  // handler calls in (verifyOtp returns before the /memberships/mine effect
  // resolves), so fetch it on demand rather than deciding against `null`.
  // `[]` is a settled answer — a brand-new buyer with no seats.
  const loadMemberships = React.useCallback(async (): Promise<AdvertiserMembership[]> => {
    if (membershipsRef.current !== null) return membershipsRef.current;
    try {
      const resp = await apiClient.get<{ memberships: AdvertiserMembership[] }>(
        '/memberships/mine',
      );
      const list = resp?.memberships ?? [];
      membershipsRef.current = list;
      setAdvertiserMemberships(list);
      return list;
    } catch (e) {
      console.warn('[useAuth] memberships fetch failed during seat resolution:', e);
      return [];
    }
  }, []);

  const currentEmail = React.useCallback(async (): Promise<string> => {
    if (!selfServiceSupabase) return '';
    const { data } = await selfServiceSupabase.auth.getSession();
    return data.session?.user?.email ?? '';
  }, []);

  const createSeat = React.useCallback(
    async (intent: WizardBusinessIntent): Promise<string> => {
      const resp = await apiClient.post<{
        membership: AdvertiserMembership;
        alreadyMember: boolean;
      }>('/memberships/ensure-self', {
        slug: intent.slug,
        businessName: intent.businessName.trim(),
        businessWebsite: intent.businessWebsite.trim() || null,
      });
      // CTV-582 eager seed: inject the membership synchronously so the wizard's
      // next commit already sees `selectedAdvertiser` populated.
      seedMembership(resp.membership);
      setSelectedAdvertiserId(resp.membership.advertiserId);
      return resp.membership.advertiserId;
    },
    [seedMembership, setSelectedAdvertiserId],
  );

  const ensureSeatForWizard = React.useCallback(
    (intent: WizardBusinessIntent): Promise<EnsureSeatOutcome> => {
      if (ensureSeatInFlightRef.current) return ensureSeatInFlightRef.current;

      const run = (async (): Promise<EnsureSeatOutcome> => {
        const typed = normalizeBusinessName(intent.businessName);
        const memberships = await loadMemberships();
        const match = memberships.find(
          (m) => normalizeBusinessName(m.advertiserName) === typed,
        );

        // 1. The buyer typed a business they already have a seat on. Use it —
        //    no server call, no question.
        if (typed && match) {
          setSelectedAdvertiserId(match.advertiserId);
          closeSeatDecision();
          await saveWizardContact(intent);
          return { status: 'ready', advertiserId: match.advertiserId };
        }

        // 2. Brand-new buyer with no seats at all. Nothing to choose between,
        //    so create what they typed (today's happy path).
        if (memberships.length === 0) {
          const advertiserId = await createSeat(intent);
          closeSeatDecision();
          await saveWizardContact(intent);
          return { status: 'ready', advertiserId };
        }

        // 3. Defensive: no business typed at all, but they have seats. Step 1
        //    requires a name, so this is unreachable in the normal flow — fall
        //    back to their first seat rather than asking about a blank name.
        if (!typed) {
          setSelectedAdvertiserId(memberships[0].advertiserId);
          closeSeatDecision();
          return { status: 'ready', advertiserId: memberships[0].advertiserId };
        }

        // 4. They typed a business that is NOT one of their seats. Ask.
        openSeatDecision();
        setPendingBusinessDecision({
          slug: intent.slug,
          // Live session, not the React `user`: the wizard captures this
          // callback on its pre-verify render, where `user` is still null.
          email: await currentEmail(),
          businessName: intent.businessName,
          businessWebsite: intent.businessWebsite,
          contactName: intent.contactName,
          contactPhone: intent.contactPhone,
          memberships,
        });
        return await new Promise<EnsureSeatOutcome>((resolve) => {
          decisionResolverRef.current = resolve;
        });
      })();

      ensureSeatInFlightRef.current = run;
      const release = () => {
        if (ensureSeatInFlightRef.current === run) ensureSeatInFlightRef.current = null;
      };
      // Settle the in-flight latch on BOTH outcomes without leaving an
      // unhandled rejection on the derived promise — the caller owns `run`'s
      // rejection.
      void run.then(release, release);
      return run;
    },
    [
      loadMemberships,
      createSeat,
      currentEmail,
      setSelectedAdvertiserId,
      openSeatDecision,
      closeSeatDecision,
    ],
  );

  // Modal → provider. Create-new has already POSTed ensure-self and seeded the
  // membership by the time we get here; use-existing did neither. Both land
  // on the same closing move: select the seat, drop the modal, release the
  // wizard's held promise.
  const resolveBusinessDecision = React.useCallback(
    (advertiserId: string) => {
      setSelectedAdvertiserId(advertiserId);
      setPendingBusinessDecision(null);
      closeSeatDecision();
      void refreshProfile();
      const resolve = decisionResolverRef.current;
      decisionResolverRef.current = null;
      resolve?.({ status: 'ready', advertiserId });
    },
    [setSelectedAdvertiserId, closeSeatDecision, refreshProfile],
  );

  // "Go back and edit the business name" (the typo path). The modal closes but
  // the decision stays OPEN — no seat is selected, so the wizard's step-1 seed
  // still shows what they typed, and the next Continue re-asks (and may now
  // match an existing seat exactly, answering itself with no modal).
  const deferBusinessDecision = React.useCallback(() => {
    setPendingBusinessDecision(null);
    const resolve = decisionResolverRef.current;
    decisionResolverRef.current = null;
    resolve?.({ status: 'deferred' });
  }, []);

  // Trigger for the header sign-in path (and a backstop for step 4's own call).
  // Split in two so the flip is caught on the render it happens — memberships
  // are still `null` then, and latching lets the decision wait for them.
  const prevUserIdRef = React.useRef<string | null>(null);
  const awaitingSeatForUserRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const curr = user?.id ?? null;
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = curr;
    if (!curr) {
      awaitingSeatForUserRef.current = null;
      return;
    }
    if (prev === curr) return;
    // Signed out → signed in. Latch UNCONDITIONALLY — do NOT test the wizard
    // intent here. The sign-in remounts the wizard body (userId is half its
    // identity key), and the body's effects run before the provider's in that
    // same commit, so the intent we'd read right now can be the empty form of a
    // body whose draft hasn't loaded. Effect B below reads the intent once the
    // memberships resolve, by which point the real value is back; a sign-in with
    // no wizard open simply finds no intent there and no-ops.
    awaitingSeatForUserRef.current = curr;
  }, [user?.id]);

  // MUST stay declared ABOVE the auto-select effect below: effects run in
  // declaration order within a commit, and this one sets `seatDecisionOpenRef`
  // synchronously so the auto-select in the SAME commit is already suppressed.
  React.useEffect(() => {
    if (advertiserMemberships === null) return; // still loading — wait
    const uid = awaitingSeatForUserRef.current;
    if (!uid || uid !== user?.id) return;
    awaitingSeatForUserRef.current = null;

    const intent = wizardIntentRef.current;
    const typed = normalizeBusinessName(intent?.businessName);
    if (!intent || !typed) return;

    const needsDecision =
      advertiserMemberships.length > 0 &&
      !advertiserMemberships.some((m) => normalizeBusinessName(m.advertiserName) === typed);
    if (needsDecision) openSeatDecision();

    void ensureSeatForWizard(intent).catch((e) => {
      console.warn('[useAuth] seat resolution after sign-in failed:', e);
    });
  }, [advertiserMemberships, user?.id, ensureSeatForWizard, openSeatDecision]);

  // Keep selectedAdvertiserId in sync with the memberships list:
  //   - if memberships load and the stored id isn't in the list anymore
  //     (revoked, switched account), fall back to the first membership
  //   - if memberships is empty, clear the selection
  //   - if memberships is null (still loading), do nothing
  //
  // CTV-638: while a business decision is open, this effect must NOT pick a
  // seat. Auto-selecting `advertiserMemberships[0]` here is mechanism #2 of the
  // three that combined to silently adopt the buyer's pre-existing advertiser —
  // it flips the wizard's identityKey, remounts the body, and re-runs the seed
  // that strips the typed business name.
  React.useEffect(() => {
    if (advertiserMemberships === null) return;
    if (seatDecisionOpenRef.current) return;
    if (advertiserMemberships.length === 0) {
      if (selectedAdvertiserId !== null) {
        setSelectedAdvertiserIdState(null);
        writeSelectedAdvertiserId(null);
      }
      return;
    }
    const stillValid = selectedAdvertiserId
      ? advertiserMemberships.some((m) => m.advertiserId === selectedAdvertiserId)
      : false;
    if (!stillValid) {
      const next = advertiserMemberships[0].advertiserId;
      setSelectedAdvertiserIdState(next);
      writeSelectedAdvertiserId(next);
    }
  }, [advertiserMemberships, selectedAdvertiserId, seatDecisionOpen]);

  // Fetch the staff user's admin-station list (super_admin → all; staff →
  // intersection of JWT scope + active grant). Empty array for advertisers.
  // The fetch fires alongside memberships so the /admin picker has data ready
  // by the time the user lands.
  const refreshMyAdminStations = React.useCallback(async () => {
    if (!user?.id) {
      setMyAdminStations(null);
      return;
    }
    try {
      const data = await apiClient.get<AdminStation[]>('/stations/my-admin');
      setMyAdminStations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn('[useAuth] my-admin fetch failed:', e);
      setMyAdminStations([]);
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (!user?.id) {
      setMyAdminStations(null);
      return;
    }
    void refreshMyAdminStations();
  }, [user?.id, refreshMyAdminStations]);

  // CTV-591: hydrate hasPassword whenever the signed-in user changes. The
  // endpoint reads auth.users.encrypted_password via the admin API — cheap
  // enough to fire on every session change. Errors don't blow up the UI;
  // callers treat null as "don't nudge yet."
  // Same stale-closure trap as refreshMemberships (CTV-582): SignInModal
  // captures this callback at the pre-verify render when `session` is null.
  // If we gated on the captured `session?.user?.id`, the post-verify call
  // would early-return null before hitting the API, and the modal would
  // never see the fresh has-password value. Read the live Supabase session
  // at call time instead — it's populated the moment verifyOtp returns.
  // Deps `[]` so the reference is stable and no captured state goes stale.
  const refreshHasPassword = React.useCallback(async (): Promise<boolean | null> => {
    if (!selfServiceSupabase) {
      setHasPassword(null);
      return null;
    }
    const { data } = await selfServiceSupabase.auth.getSession();
    const currentUserId = data.session?.user?.id;
    if (!currentUserId) {
      setHasPassword(null);
      return null;
    }
    try {
      const resp = await apiClient.get<{ hasPassword: boolean }>('/auth/me/has-password');
      const value = Boolean(resp.hasPassword);
      setHasPassword(value);
      return value;
    } catch (e) {
      console.warn('[useAuth] has-password fetch failed:', e);
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (!session?.user?.id) {
      setHasPassword(null);
      return;
    }
    void refreshHasPassword();
  }, [session?.user?.id, refreshHasPassword]);

  // Fetch the Phase 2b publisher-admin grant. The RPC ships in this branch
  // (migration 20260511160000), so any error here is a real failure — RPC
  // missing on this project, network outage, perms misconfigured. Deny by
  // default in those cases; the access-denied page is friendlier than
  // accidentally letting in a revoked-grant staff user.
  React.useEffect(() => {
    if (!session?.user?.id || !selfServiceSupabase) {
      setHasPublisherAdminGrant(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await selfServiceSupabase.rpc('has_publisher_admin_grant');
        if (cancelled) return;
        if (error) {
          console.warn('[useAuth] has_publisher_admin_grant rpc errored — denying:', error);
          setHasPublisherAdminGrant(false);
          return;
        }
        setHasPublisherAdminGrant(Boolean(data));
      } catch (e) {
        if (cancelled) return;
        console.warn('[useAuth] has_publisher_admin_grant threw — denying:', e);
        setHasPublisherAdminGrant(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const value: AuthContextValue = React.useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      advertiserMemberships,
      refreshMemberships,
      seedMembership,
      selectedAdvertiser,
      setSelectedAdvertiserId,
      registerWizardIntent,
      ensureSeatForWizard,
      pendingBusinessDecision,
      hasPublisherAdminGrant,
      refreshProfile,
      myAdminStations,
      refreshMyAdminStations,
      refreshHasPassword,

      signOut: async () => {
        if (selfServiceSupabase) {
          // Revoke only this tab's session. supabase-js defaults to
          // scope 'global', which deletes every session the user holds on
          // the shared Supabase project, including an admin's session on
          // the dashboard origin. That is how "Log in as" broke for a super
          // admin on 2026-08-20: logging out of Streamline as themselves
          // killed their dashboard session server-side, and the dashboard
          // tab kept rendering on its unexpired JWT until the next
          // session-checked call (the impersonate-user edge function)
          // came back 401.
          await selfServiceSupabase.auth.signOut({ scope: 'local' });
        }
        clearWizardCaches();
        // CTV-759: the remembered Station scope + collapse target are
        // sessionStorage, so a second sign-in in the same tab would
        // otherwise inherit the previous user's station.
        clearAdminScopeMemory();
        setSession(null);
        setProfile(null);
        setAdvertiserMemberships(null);
        setSelectedAdvertiserIdState(null);
        setHasPublisherAdminGrant(null);
        setMyAdminStations(null);
        setHasPassword(null);
        // CTV-638: a decision belongs to the session that raised it. Signing
        // out settles any held promise so the wizard isn't left awaiting an
        // answer nobody can give.
        setPendingBusinessDecision(null);
        closeSeatDecision();
        awaitingSeatForUserRef.current = null;
        const resolveDecision = decisionResolverRef.current;
        decisionResolverRef.current = null;
        resolveDecision?.({ status: 'deferred' });
      },

      updateProfile: async (patch) => {
        if (!user || !session?.user?.id) {
          // Anonymous callers (e.g. wizard step 1 before sign-in) silently
          // no-op — react-hook-form already holds the in-flight values until
          // the user authenticates. Once signed in, future updateProfile
          // calls persist to /profiles/me.
          return;
        }
        // Optimistic local merge — the wizard surfaces these values
        // immediately. The PUT below reconciles with the canonical row.
        const next: AdvertiserProfile = {
          userId: user.id,
          contactEmail: profile?.contactEmail ?? user.email,
          contactName: patch.contactName ?? profile?.contactName ?? user.contactName ?? null,
          contactPhone: patch.contactPhone ?? profile?.contactPhone ?? user.contactPhone ?? null,
        };
        setProfile(next);
        try {
          const saved = await apiClient.put<AdvertiserProfile>('/profiles/me', {
            contactName: next.contactName,
            contactPhone: next.contactPhone,
          });
          setProfile(saved);
        } catch (e) {
          console.warn('[useAuth] updateProfile failed:', e);
        }
      },

      requestOtp: async (email, stationSlug) => {
        if (!selfServiceSupabase) {
          throw new Error(
            'VITE_SELFSERVICE_SUPABASE_URL / VITE_SELFSERVICE_SUPABASE_ANON_KEY are not configured.',
          );
        }
        analytics.track(EVENTS.OTP_REQUESTED, { station_slug: stationSlug });

        // In dev (local AND the dev Render deploy), hit the NestJS dev-only
        // endpoint that uses the Supabase admin API to generate a magiclink
        // AND surfaces the 6-digit OTP back to the client. Saves the email
        // round-trip when the prod email links would be misrouted / email
        // isn't wired up yet. The endpoint also sends the email so users can
        // fall back to it if needed. isSelfServiceDevelopment() is true on
        // localhost and selfservice-frontend-dev.onrender.com, false in prod.
        if (isSelfServiceDevelopment()) {
          try {
            const { otp } = await apiClient.post<{ otp: string }>(
              '/auth/dev/issue-otp',
              { email, stationSlug },
              { anonymous: true },
            );
            return { devCode: otp };
          } catch (e) {
            if (!(e instanceof ApiError) || e.status !== 403) {
              console.warn('[useAuth] dev OTP endpoint failed; falling back to email:', e);
            }
          }
        }

        // Our backend mints the Supabase token via admin.generateLink and
        // dispatches the SendGrid `auth.signin` template. The stationSlug
        // (when present) drives per-station branding on the email.
        await apiClient.post(
          '/auth/sign-in/request',
          { email, stationSlug, redirectTo: `${window.location.origin}` },
          { anonymous: true },
        );
        return { devCode: '' };
      },

      verifyOtp: async (email, code) => {
        if (!selfServiceSupabase) {
          throw new Error('Supabase Auth not configured.');
        }
        const { data, error } = await selfServiceSupabase.auth.verifyOtp({
          email,
          token: code,
          type: 'email',
        });
        if (error) {
          analytics.track(EVENTS.OTP_FAILED, { reason: 'invalid_code' });
          throw new Error(error.message);
        }
        if (!data.session?.user) {
          analytics.track(EVENTS.OTP_FAILED, { reason: 'no_session' });
          throw new Error('OTP verified but no session returned.');
        }
        analytics.track(EVENTS.OTP_VERIFIED);
        // onAuthStateChange will pick up the new session asynchronously, but
        // we also synthesize the SelfServiceUser eagerly so the caller can
        // show "signed in as …" without waiting for a render. Profile fields
        // populate on the next render once /profiles/me resolves.
        const eager: SelfServiceUser = {
          id: data.session.user.id,
          email: data.session.user.email ?? email,
          role: mapRole(data.session.user),
          rawRole: extractRawRole(data.session.user),
          organizationId: extractOrganizationId(data.session.user),
          publisherSlug: extractStations(data.session.user)[0],
          hasPassword: null,
        };
        return eager;
      },

      canAdminStation: (config) => {
        if (!user || !user.rawRole) return false;
        if (user.rawRole === 'super_admin') return true;
        if (user.rawRole === 'organization_admin') {
          return !!config.organizationId && user.organizationId === config.organizationId;
        }
        if (user.rawRole === 'manager' || user.rawRole === 'sales_rep') {
          const claims = extractStations(session?.user);
          // config.name is the raw stations.name (= what JWT entries carry).
          return claims.includes(config.name);
        }
        return false;
      },
    }),
    [
      user,
      session,
      profile,
      advertiserMemberships,
      refreshMemberships,
      seedMembership,
      selectedAdvertiser,
      setSelectedAdvertiserId,
      registerWizardIntent,
      ensureSeatForWizard,
      pendingBusinessDecision,
      closeSeatDecision,
      hasPublisherAdminGrant,
      refreshProfile,
      myAdminStations,
      refreshMyAdminStations,
      refreshHasPassword,
    ],
  );

  // Don't render children until we've at least tried to read the existing
  // session — otherwise initial paint flashes "signed out" before the
  // persisted session loads.
  if (!bootstrapped) return null;

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* CTV-638: rendered from the provider, above the wizard's identity-keyed
          body, so the sign-in that raises the question can't destroy the
          question. Portals to <body> from inside. */}
      {pendingBusinessDecision && (
        <BusinessDecisionModal
          decision={pendingBusinessDecision}
          onSeatCreated={seedMembership}
          onResolved={resolveBusinessDecision}
          onDeferred={deferBusinessDecision}
        />
      )}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
